'use client';

import {useSearchParams} from 'next/navigation';

import type {FilterConfig} from '@/modules/commerce/filters/client';
import ClientFilters from '@/modules/commerce/filters/client';
import type {
	VehicleBrandAllFilterOptionsQueryResult,
	VehicleModelAllFilterOptionsQueryResult,
	VehicleTypeAllFilterOptionsQueryResult,
} from '@/sanity/schemas/types';

type VehicleTypeFilter = NonNullable<VehicleTypeAllFilterOptionsQueryResult>;
type VehicleBrandFilter = NonNullable<VehicleBrandAllFilterOptionsQueryResult>;
type VehicleModelFilter = NonNullable<VehicleModelAllFilterOptionsQueryResult>;

type Props = {
	vehicleTypeFilter: VehicleTypeFilter;
	vehicleBrandFilter: VehicleBrandFilter;
	vehicleModelFilter: VehicleModelFilter;
};

const mapToFilterOptions = (
	filter: VehicleTypeFilter | VehicleBrandFilter | VehicleModelFilter,
) => {
	return filter.map((option) => ({
		label: option.name ?? '',
		value: option.slug?.current ?? '',
	}));
};

export const ClientInteriorFilters = ({
	vehicleTypeFilter,
	vehicleBrandFilter,
	vehicleModelFilter,
}: Props) => {
	const searchParams = useSearchParams();

	const vehicleTypeSlugs = searchParams.getAll('kjoretoy');
	let vehicleBrandSlugs = searchParams.getAll('merke');

	// Always allow filtering on any vehichle type:
	const vehicleTypeFilterConfig: FilterConfig | null = {
		id: 'kjoretoy',
		label: 'Kjøretøy',
		options: mapToFilterOptions(vehicleTypeFilter).sort((a, b) =>
			a.label.localeCompare(b.label),
		),
	};

	// Only allow filtering on brands that exist for the selected vehicle type (if any):
	let vehicleBrandFilterConfig: FilterConfig | null = null;
	if (vehicleTypeSlugs?.length) {
		const brandsInVehichleType = vehicleBrandFilter.filter((option) => {
			const slug = option.vehicleType?.slug?.current;
			if (!slug) {
				return false;
			}

			return vehicleTypeSlugs?.includes(slug);
		});

		vehicleBrandFilterConfig = {
			id: 'merke',
			label: 'Merke',
			options: mapToFilterOptions(brandsInVehichleType).sort((a, b) =>
				a.label.localeCompare(b.label),
			),
		};

		// Remove brands from the search params that are not valid for the selected vehicle type:
		vehicleBrandSlugs = vehicleBrandSlugs.filter((m) =>
			brandsInVehichleType.some((f) => f.slug?.current === m),
		);
	}

	// Only allow filtering on brands that exist for the selected vehicle brands (if any):
	let vehicleModelFilterConfig: FilterConfig | null = null;
	if (vehicleBrandSlugs?.length) {
		const filter = vehicleModelFilter.filter((option) => {
			const slug = option.vehicleBrand?.slug?.current;
			if (!slug) {
				return false;
			}

			return vehicleBrandSlugs?.includes(slug);
		});

		vehicleModelFilterConfig = {
			id: 'modell',
			label: 'Modell',
			options: mapToFilterOptions(filter).sort((a, b) => a.label.localeCompare(b.label)),
		};
	}

	return (
		<ClientFilters
			filterConfigs={[
				vehicleTypeFilterConfig,
				vehicleBrandFilterConfig,
				vehicleModelFilterConfig,
			].filter(Boolean)}
		/>
	);
};
